import { useState } from 'react'
import axios from 'axios'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const REASONS = [
  {
    value: 'copyright',
    label: 'Copyright violation',
    desc: 'This work reproduces copyrighted content without the owner\'s permission.',
  },
  {
    value: 'inappropriate',
    label: 'Inappropriate content',
    desc: 'Contains sexual, violent, or offensive content that violates community guidelines.',
  },
  {
    value: 'spam',
    label: 'Spam or low-quality',
    desc: 'Fake engagement, plagiarised filler, or AI-generated spam content.',
  },
  {
    value: 'harassment',
    label: 'Harassment or abuse',
    desc: 'Targets a real person with abuse, threats, or doxxing.',
  },
  {
    value: 'misinformation',
    label: 'Harmful misinformation',
    desc: 'Contains demonstrably false information that could cause real-world harm.',
  },
  {
    value: 'other',
    label: 'Other',
    desc: 'Something else not covered above.',
  },
]

export default function ReportModal({ contentType = 'story', contentId, contentTitle, onClose }) {
  const { user } = useAuth()
  const [reason, setReason]   = useState('')
  const [details, setDetails] = useState('')
  const [status, setStatus]   = useState('idle') // idle | submitting | success | error

  async function handleSubmit(e) {
    e.preventDefault()
    if (!reason) return
    setStatus('submitting')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      await axios.post('/api/reports', {
        content_type: contentType,
        content_id:   String(contentId),
        reason,
        details:      details.trim() || undefined,
      }, {
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : {},
      })
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="report-modal__overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }} onKeyDown={(e) => { if (e.key === 'Escape') onClose() }} tabIndex={-1}>
      <div className="report-modal" role="dialog" aria-modal="true" aria-label="Report content">

        <div className="report-modal__header">
          <h2 className="report-modal__title">Report content</h2>
          <button className="report-modal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {status === 'success' ? (
          <div className="report-modal__success">
            <div className="report-modal__success-icon">✓</div>
            <h3>Report submitted</h3>
            <p>Thank you — our moderation team will review this within 5 business days.</p>
            {reason === 'copyright' && (
              <p className="report-modal__success-dmca">
                For formal DMCA takedown notices, please also email{' '}
                <a href="mailto:support@versesite.com">support@versesite.com</a> or visit our{' '}
                <Link to="/dmca" onClick={onClose}>DMCA policy page</Link>.
              </p>
            )}
            <button className="btn btn-primary" onClick={onClose} style={{ marginTop: '16px' }}>
              Done
            </button>
          </div>
        ) : !user ? (
          <div>
            <div className="report-modal__login-notice">
              <Link to="/login">Sign in</Link> to submit a report. Anonymous reports cannot be processed.
            </div>
            <div className="report-modal__footer">
              <button type="button" className="btn" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {contentTitle && (
              <p className="report-modal__target">
                Reporting: <strong>"{contentTitle}"</strong>
              </p>
            )}

            <div className="report-modal__reasons">
              {REASONS.map((r) => (
                <label key={r.value} className={`report-reason${reason === r.value ? ' report-reason--selected' : ''}`}>
                  <input
                    type="radio"
                    name="reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={() => setReason(r.value)}
                  />
                  <div className="report-reason__body">
                    <span className="report-reason__label">{r.label}</span>
                    <span className="report-reason__desc">{r.desc}</span>
                  </div>
                </label>
              ))}
            </div>

            {reason === 'copyright' && (
              <div className="report-modal__copyright-note">
                <strong>Copyright claim?</strong> For a formal DMCA takedown, please also visit our{' '}
                <Link to="/dmca" onClick={onClose}>DMCA policy page</Link> and email{' '}
                <a href="mailto:support@versesite.com">support@versesite.com</a>.
              </div>
            )}

            <div className="report-modal__field">
              <label className="report-modal__field-label">
                Additional details <span className="report-modal__field-optional">(optional)</span>
              </label>
              <textarea
                className="report-modal__textarea"
                placeholder="Describe the issue, link to the original work if relevant…"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                maxLength={1000}
                rows={4}
              />
              <span className="report-modal__char-count">{details.length}/1000</span>
            </div>

            {status === 'error' && (
              <p className="report-modal__error">Something went wrong — please try again.</p>
            )}

            <div className="report-modal__footer">
              <button type="button" className="btn" onClick={onClose}>Cancel</button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!reason || status === 'submitting'}
              >
                {status === 'submitting' ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
